import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import { format, addDays } from 'date-fns';
import { useAuth } from '../components/context/AuthContext';
import { appointmentService } from './AppointmentsService';
import AppointmentForm from './AppointmentForm';
import AppointmentDetails from './AppointmentDetails';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import { doctorService } from './DoctorsService';
import axios from 'axios';

const localizer = momentLocalizer(moment);

// Custom event styling based on event type
const eventStyleGetter = (event) => {
  const isAvailableSlot = event.isAvailable;
  
  if (isAvailableSlot) {
    return {
      style: {
        backgroundColor: '#10B981', // Green for available
        borderLeft: '4px solid #047857',
        borderRadius: '4px',
        opacity: 0.9,
        color: '#fff',
        display: 'block',
        cursor: 'pointer',
        fontWeight: '500'
      }
    };
  } else {
    return {
      style: {
        backgroundColor: '#3B82F6', // Blue for booked
        borderRadius: '4px',
        opacity: 0.9,
        color: '#fff',
        border: '0px',
        display: 'block',
        cursor: 'pointer'
      }
    };
  }
};

const AppointmentScheduler = () => {
    const [appointments, setAppointments] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);  // New state for details modal
    const [selectedAppointment, setSelectedAppointment] = useState(null);  // New state for selected appointment
    const [isLoading, setIsLoading] = useState(true);
    const [doctors, setDoctors] = useState([]);
    const [viewRange, setViewRange] = useState({
      start: new Date(),
      end: addDays(new Date(), 7) // Default to show a week
    });
    const { currentUser } = useAuth();


// Debug when calendarEvents changes
// useEffect(() => {
//     console.log(`Calendar events updated: ${calendarEvents.length} total events`);
//     console.log(`- ${appointments.length} appointments`);
//     console.log(`- ${availableSlots.length} available slots`);
    
//     // Print some sample events if available
//     if (availableSlots.length > 0) {
//       console.log('Sample available slot:', availableSlots[0]);
//     }
//     if (appointments.length > 0) {
//       console.log('Sample appointment:', appointments[0]);
//     }
//   }, [calendarEvents]);

  // Fetch appointments and doctors
  useEffect(() => {
    const fetchAppointments = async () => {
        try {
          setIsLoading(true);
          let data = [];
      
          // Add null check before accessing currentUser.role
          if (currentUser) {
            if (currentUser.role === 'doctor') {
              data = await appointmentService.getAppointmentsByDoctor(currentUser._id);
            } else {
              data = await appointmentService.getAppointmentsByUser(currentUser._id);
            }
      
            // Transform the data for the calendar
            const formattedAppointments = data.map(apt => ({
              id: apt._id,
              title: apt.title || 'Appointment',
              start: new Date(apt.startTime),
              end: new Date(apt.endTime),
              resource: apt,
              isAvailable: false
            }));
      
            setAppointments(formattedAppointments);
          }
        } catch (error) {
          console.error('Error fetching appointments:', error);
        } finally {
          setIsLoading(false);
        }
      };

    const fetchDoctors = async () => {
      try {
       
        const response = await axios.get('http://localhost:5001/api/doctors/');
        const data = await response.data.data.data;
        setDoctors(data);
      } catch (error) {
        console.error('Error fetching doctors:', error);
      }
    };
    console.log(doctors)

    fetchAppointments();
  if (currentUser?.role !== 'doctor') {  // Use optional chaining
    fetchDoctors();
  }
}, [currentUser?._id, currentUser?.role]);  // Use optional chaining in dependencies
  // Fetch available slots when view range or doctors change
  useEffect(() => {
  // Focus on updating the fetchAvailableSlots function

  const fetchAvailableSlots = async () => {
    try {
      // Clear previous slots when fetching new ones
      setAvailableSlots([]);
      
      if (!currentUser || currentUser.role === 'patient') return;
      
      if (currentUser.role === 'doctor') {
        // If current user is a doctor, fetch available slots for just this doctor
        const doctorSlots = await fetchDoctorAvailableSlots(currentUser._id, {
          firstName: currentUser.firstName || '',
          lastName: currentUser.lastName || '',
          specialty: currentUser.specialty || ''
        });
        
        setAvailableSlots(doctorSlots);
      } else {
        // Make sure doctors array exists and has items before proceeding
        if (!doctors || doctors.length === 0) {
          console.log('No doctors available to fetch slots');
          return;
        }
        
        // If patient, fetch available slots for all doctors
        const allSlots = [];
        
        // Show loading state
        setIsLoading(true);
        
        for (const doctor of doctors) {
          if (doctor && doctor._id) { // Make sure doctor has an ID
            const slots = await fetchDoctorAvailableSlots(doctor._id, doctor);
            allSlots.push(...slots);
          }
        }
        
        setAvailableSlots(allSlots);
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDoctorAvailableSlots = async (doctorId, doctorInfo = null) => {
    try {
      const slots = [];
      
      // For each day in the view range
      let currentDate = new Date(viewRange.start);
      while (currentDate <= viewRange.end) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        
        console.log(`Fetching available slots for doctor ${doctorId} on ${dateStr}`);
        const availableSlotsForDay = await appointmentService.getAvailableSlots(doctorId, dateStr);
        console.log(`Got ${availableSlotsForDay.length} slots for ${dateStr}`);
        
        // Transform slots to calendar events
        const calendarSlots = availableSlotsForDay.map(slot => ({
          id: `available-${doctorId}-${new Date(slot.startTime).getTime()}`,
          title: doctorInfo 
            ? `Available: Dr. ${doctorInfo.lastName || 'Doctor'}`
            : 'Available Slot',
          start: new Date(slot.startTime),
          end: new Date(slot.endTime),
          isAvailable: true,
          doctorId: doctorId,
          doctorInfo: doctorInfo
        }));
        
        slots.push(...calendarSlots);
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      return slots;
    } catch (error) {
      console.error(`Error fetching available slots for doctor ${doctorId}:`, error);
      return [];
    }
  };

    if (!isLoading) {
      fetchAvailableSlots();
    }
  }, [viewRange, doctors, isLoading, currentUser]);

  // Combine appointments and available slots for calendar display
  useEffect(() => {
    setCalendarEvents([...appointments, ...availableSlots]);
  }, [appointments, availableSlots]);

  const handleViewRangeChange = (range) => {
    // When the calendar view changes, update our view range
    setViewRange({
      start: range.start,
      end: range.end
    });
  };

  const handleSelectSlot = (slotInfo) => {
    setSelectedSlot(slotInfo);
    setIsFormOpen(true);
  };
  const handleSelectEvent = (event) => {
    if (event.isAvailable) {
      // This is an available slot - open form to book it
      setSelectedSlot({
        start: event.start,
        end: event.end,
        doctorId: event.doctorId,
        doctorInfo: event.doctorInfo,
        isAvailableSlot: true
      });
      setIsFormOpen(true);
    } else {
      // This is an existing appointment - open details view
      setSelectedAppointment(event);
      setIsDetailsOpen(true);
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedSlot(null);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedAppointment(null);
  };

  const handleEditAppointment = (appointment) => {
    // Set up for editing
    setSelectedSlot(appointment);
    setIsDetailsOpen(false); // Close details view
    setIsFormOpen(true); // Open edit form
  };


  const handleSaveAppointment = async (appointmentData) => {
    try {
      let savedAppointment;
      
      if (appointmentData.id) {
        // Update existing appointment
        savedAppointment = await appointmentService.updateAppointment(
          appointmentData.id, 
          appointmentData
        );
      } else {
        // Create new appointment
        savedAppointment = await appointmentService.createAppointment(appointmentData);
      }
      
      // Refresh appointments list
      if (currentUser.role === 'doctor') {
        const data = await appointmentService.getAppointmentsByDoctor(currentUser._id);
        setAppointments(data.map(apt => ({
          id: apt._id,
          title: apt.title || 'Appointment',
          start: new Date(apt.startTime),
          end: new Date(apt.endTime),
          resource: apt,
          isAvailable: false
        })));
      } else {
        const data = await appointmentService.getAppointmentsByUser(currentUser._id);
        setAppointments(data.map(apt => ({
          id: apt._id,
          title: apt.title || 'Appointment',
          start: new Date(apt.startTime),
          end: new Date(apt.endTime),
          resource: apt,
          isAvailable: false
        })));
      }
      
      // Also refresh available slots as the new booking may have taken a slot
      setAvailableSlots(availableSlots.filter(slot => 
        slot.id !== `available-${appointmentData.doctorId}-${appointmentData.startTime.getTime()}`
      ));
      
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error saving appointment:', error);
    }
  };

  const handleDeleteAppointment = async (id) => {
    try {
      await appointmentService.deleteAppointment(id);
      setAppointments(appointments.filter(apt => apt.id !== id));
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Appointment Scheduler</h1>
      
      <div className="flex items-center mb-4 justify-between">
        <div>
          <p className="text-sm flex items-center">
            <span className="inline-block w-3 h-3 rounded-full mr-2 bg-blue-500"></span>
            Your Appointments
          </p>
          <p className="text-sm flex items-center">
            <span className="inline-block w-3 h-3 rounded-full mr-2 bg-green-500"></span>
            Available Slots
          </p>
        </div>
        
        <div className="text-sm text-gray-600">
          Click on any available slot to book
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center my-10">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="h-[600px] bg-white rounded-lg shadow-md p-4">
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            selectable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onRangeChange={handleViewRangeChange}
            eventPropGetter={eventStyleGetter}
            className="rounded-md"
            defaultView={Views.WEEK}
            views={['month', 'week', 'day']}
          />
        </div>
      )}

      {isFormOpen && (
        <AppointmentForm
          isOpen={isFormOpen}
          onClose={handleCloseForm}
          onSave={handleSaveAppointment}
          onDelete={handleDeleteAppointment}
          selectedSlot={selectedSlot}
          doctors={doctors}
          currentUser={currentUser}
        />
      )}
       {/* New AppointmentDetails component */}
       {isDetailsOpen && (
        <AppointmentDetails
          appointment={selectedAppointment}
          isOpen={isDetailsOpen}
          onClose={handleCloseDetails}
          onEdit={handleEditAppointment}
          onDelete={handleDeleteAppointment}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default AppointmentScheduler;